import { useState } from "react";
import { Calendar } from "lucide-react";

interface ScopeSelectionProps {
  onNext: (scope: ExportScope) => void;
  onBack: () => void;
}

export interface ExportScope {
  startDate: string;
  endDate: string;
  mandateIds: string[];
  includeUnclassified: boolean;
}

export const ScopeSelection = ({ onNext, onBack }: ScopeSelectionProps) => {
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });

  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [includeUnclassified, setIncludeUnclassified] = useState(false);

  const handleContinue = () => {
    onNext({
      startDate,
      endDate,
      mandateIds: [], // TODO: Add mandate selector when multi-mandate support is added
      includeUnclassified
    });
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-brand-primary mb-2 text-center">
        Select Export Scope
      </h3>
      <p className="text-brand-text-secondary text-sm mb-6 text-center">
        Choose date range and options for your export
      </p>

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Date Range */}
        <div className="bg-white p-6 rounded-xl border border-brand-border">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="h-5 w-5 text-brand-primary" />
            <h4 className="font-bold text-brand-text-primary">Date Range</h4>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-text-secondary mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-brand-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-text-secondary mb-2">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-brand-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-accent"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => {
                const date = new Date();
                setStartDate(new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0]);
                setEndDate(new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0]);
              }}
              className="px-3 py-1.5 text-xs bg-brand-secondary text-brand-primary rounded-lg hover:bg-brand-secondary/80"
            >
              This Month
            </button>
            <button
              onClick={() => {
                const date = new Date();
                setStartDate(new Date(date.getFullYear(), date.getMonth() - 1, 1).toISOString().split('T')[0]);
                setEndDate(new Date(date.getFullYear(), date.getMonth(), 0).toISOString().split('T')[0]);
              }}
              className="px-3 py-1.5 text-xs bg-brand-secondary text-brand-primary rounded-lg hover:bg-brand-secondary/80"
            >
              Last Month
            </button>
            <button
              onClick={() => {
                const date = new Date();
                setStartDate(new Date(date.getFullYear(), 0, 1).toISOString().split('T')[0]);
                setEndDate(new Date(date.getFullYear(), 11, 31).toISOString().split('T')[0]);
              }}
              className="px-3 py-1.5 text-xs bg-brand-secondary text-brand-primary rounded-lg hover:bg-brand-secondary/80"
            >
              This Year
            </button>
          </div>
        </div>

        {/* Options */}
        <div className="bg-white p-6 rounded-xl border border-brand-border">
          <h4 className="font-bold text-brand-text-primary mb-4">Export Options</h4>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeUnclassified}
                onChange={(e) => setIncludeUnclassified(e.target.checked)}
                className="w-4 h-4 text-brand-primary border-brand-border rounded focus:ring-brand-accent"
              />
              <div>
                <span className="text-sm font-medium text-brand-text-primary">
                  Include Unclassified Transactions
                </span>
                <p className="text-xs text-brand-text-secondary">
                  Export transactions that haven't been assigned to an account yet
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <i className="fa-solid fa-info-circle text-blue-600"></i>
            <h5 className="font-bold text-blue-900">Export Summary</h5>
          </div>
          <p className="text-sm text-blue-800">
            Exporting transactions from <strong>{new Date(startDate).toLocaleDateString('de-CH')}</strong> to{' '}
            <strong>{new Date(endDate).toLocaleDateString('de-CH')}</strong>
            {includeUnclassified && <span className="ml-1">(including unclassified)</span>}
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-center gap-4 mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-brand-secondary text-brand-primary rounded-lg font-medium hover:bg-brand-secondary/80 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-3 bg-brand-primary text-white rounded-lg font-medium hover:bg-brand-primary/90 transition-colors"
        >
          Continue to Validation
        </button>
      </div>
    </div>
  );
};
